import { CommonModule } from '@angular/common';
import {
  AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation,
} from '@angular/core';

import { MdbTableModule } from 'mdb5-adapted';
import { NgxPaginationModule, PaginationInstance } from 'ngx-pagination';
import { Subscription } from 'rxjs';

import { LmsPaginateComponent } from '../lms-paginate/lms-paginate.component';

@Component({
  selector: 'lms-gridview',
  styleUrls: ['./lms-gridview.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
  <table
    mdbTable
    #table="mdbTable"
    mdbTableSort
    #sort="mdbTableSort"
    [sort]="sort"
    [dataSource]="table_data_source"
    class="table table-auto table-borderless table-devider table-light table-hover table-sm shadow-1-strong rounded-1 p-1">
    <thead>
      <tr class="table-primary bg-primary bg-gradient table-center-header text-wrap">
        <th
          *ngFor="let column of (table_config?.columns || [])"
          [mdbTableSortHeader]="column.key"
          [ngClass]="{ 'numeric-col': !!column.numeric }"
        >
          {{ column.heading }}
        </th>
        <th *ngIf="table_config?.ediTable?.edit">View</th>
        <th *ngIf="table_config?.ediTable?.add">Add</th>
      </tr>
    </thead>

    <tbody>
      <tr
        *ngFor="let data of table.data | paginate: config; let i = index"
        class="text-wrap"
      >
        <td
          *ngFor="let column of (table_config?.columns || [])"
          [ngClass]="{ 'numeric-col': !!column.numeric, 'date-col': !!column.datecol }"
        >
          <ng-container *ngIf="!!column.datecol">
            <span class="date-col">{{ data[column.key] | date: 'yyyy-MM-dd HH:mm:ss' }}</span>
          </ng-container>

          <ng-container *ngIf="!!column.status">
            <span
              *ngIf="data[column.key] === 'Active'"
              class="badge badge-primary rounded-pill d-inline"
            >{{ data[column.key] }}</span>
            <span
              *ngIf="data[column.key] === 'New' || data[column.key] === 'Pending'"
              class="badge badge-info rounded-pill d-inline"
            >{{ data[column.key] }}</span>
            <span
              *ngIf="data[column.key] === 'Submitted'"
              class="badge badge-success rounded-pill d-inline"
            >{{ data[column.key] }}</span>
            <span
              *ngIf="data[column.key] === 'Cancelled' || data[column.key] === 'Rejected'"
              class="badge badge-dark text-bg-warning rounded-pill d-inline"
            >{{ data[column.key] }}</span>
            <span
              *ngIf="data[column.key] === 'Disbursed'"
              class="badge badge-secondary rounded-pill d-inline"
            >{{ data[column.key] }}</span>
            <span
              *ngIf="data[column.key] === 'NuPay' || data[column.key] === 'Journal'"
              class="badge text-emphasis badge-secondary rounded-pill d-inline"
            >{{ data[column.key] }}</span>
          </ng-container>

          <ng-container *ngIf="!column.datecol && !column.status">
            <span>{{ data[column.key] }}</span>
          </ng-container>
        </td>

        <td
          *ngIf="table_config?.ediTable?.edit"
          class="row-action-column"
          style="width: 75px;"
        >
          <a
            type="button"
            class="btn btn-block btn-primary"
            data-mdb-ripple-color="#ffffff"
            aria-label="View"
            [textContent]="data.status === 'Submitted' ? 'View' : 'Continue'"
            (click)="viewSelectedItem(data)"
          ></a>
        </td>

        <td
          *ngIf="table_config?.ediTable?.add"
          class="row-action-column"
          style="white-space: nowrap"
        >
          <button
            class="lms-table__action-button"
            title="Add New Row"
            aria-label="Add New Row"
            (click)="addRow()"
          >
            <span class="material-icons"> playlist_add </span>
          </button>
        </td>
      </tr>
    </tbody>
  </table>
  <lms-paginate [config]="config" (changePaged)="setPage($event)"></lms-paginate>`,
  imports: [
    CommonModule,
    MdbTableModule,
    NgxPaginationModule,
    LmsPaginateComponent
],
  standalone: true,
})
export class LmsGridviewComponent implements OnInit, AfterViewInit, OnDestroy {
  config: PaginationInstance = {
    id: 'pager1',
    itemsPerPage: 10,
    currentPage: 1,
  };

  @Input() id: string;
  @Input() table_config: any;
  @Output() onRowSelect = new EventEmitter<any>();
  @Output() onRowAdd = new EventEmitter<any>();
  @Output() onRowEdit = new EventEmitter<any>();
  @Output() onLoaded = new EventEmitter<number>();

  displayed_columns!: string[];
  private data_change_sub!: Subscription;
  table_data_source: any[] = [];
  updated_row_index = -1;
  ngOnInit(): void {
    if (this.table_config && Array.isArray(this.table_config.columns)) {
      this.setDisplayedColumns(this.table_config.columns);
    } else {
      this.displayed_columns = [];
    }
    if (this.table_config && this.table_config.table_data_changer && typeof this.table_config.table_data_changer.subscribe === 'function') {
      this.trackDataChange();
    }
  }

  ngAfterViewInit(): void {
    this.config.id = 'pgr_'+this.id;
  }

  trackDataChange() {
    try {
      this.data_change_sub = this.table_config.table_data_changer.subscribe({
      next: (new_data: any) => {
        if (new_data) {
          setTimeout(() => {
            this.table_data_source = new_data;
            if (new_data.highlight) {
              this.goToUpdatedPage(new_data.highlight, new_data);
            }

            this.config.totalItems = new_data.length;
            this.onLoaded.emit(new_data.length);
          }, 100);
        }
      },
      });
    } catch (e) {
      this.data_change_sub = undefined as any;
    }
  }

  goToUpdatedPage(updated_row: any, data: any[]) {
    const updated_index = data.findIndex((row) => {
      let is_matching = true;
      const primary_key_count = this.table_config.primary_key_set.length;
      for (let i = 0; i < primary_key_count; i++) {
        const column = this.table_config.primary_key_set[i];
        if (row[column] != updated_row[column]) {
          is_matching = false;
          break;
        }
      }
      return is_matching;
    });
    setTimeout(() => {
      if (updated_index >= 0) {
        setTimeout(() => {
          this.updated_row_index = -1;
        }, 1000);
      }
    }, 100);
  }

  onTableDataChange(event: any) {
    this.config.totalItems = Array.isArray(event) ? event.length : 0;
  }

  onTableSizeChange(event: any): void {
    this.config.itemsPerPage = Number(event.target.value) || this.config.itemsPerPage;
    this.config.currentPage = 1;
  }

  setDisplayedColumns(column_config: any[]) {
    const col_count = column_config.length;
    const columns_to_display: any[] = [];
    for (let i = 0; i < col_count; i++) {
      const col_config = column_config[i];
      columns_to_display.push(col_config.key);
    }

    if (this.table_config?.ediTable?.edit) {
      columns_to_display.push('edit');
    }

    this.displayed_columns = columns_to_display;
  }

  setPage(page: number) {
    if (this.config.currentPage !== page) {
      this.config.currentPage = page;
    }
  }

  addRow() {
    this.onRowAdd.emit();
  }

  viewSelectedItem(row: any) {
    this.onRowSelect.emit(row);
  }

  ngOnDestroy(): void {
    if (this.data_change_sub && typeof (this.data_change_sub.unsubscribe) === 'function') {
      try {
        this.data_change_sub.unsubscribe();
      } catch (e) {
      }
    }
  }
}
